import type { ReadyBriefItem } from "../types/workflow";

/** Reads ONE brief by its RFC 5322 id and triage ROW key (orchestratorApi.getReadyBrief). */
export type ReadBrief = (
  messageIdentity: string,
  mailboxAddress: string | null,
) => Promise<ReadyBriefItem>;

/** What the pane says when a card's brief is gone (Ruling R20/R26, 2026-10-06). */
export const BRIEF_GONE_NOTICE = "This email was already started or dismissed.";

/**
 * What a card's click resolves to:
 *   - "detail"   the full brief was read; open it.
 *   - "full"     the card already IS the full brief; open it, no read.
 *   - "fallback" the read failed (not a 404); open the CARD, refresh the list.
 *   - "gone"     404 — launched or ignored since the list was read; do NOT open.
 */
export type ReadyBriefOpen =
  | { outcome: "detail" | "full" | "fallback"; brief: ReadyBriefItem }
  | { outcome: "gone" };

function isNotFound(err: unknown): boolean {
  // orchestratorApi's ApiError carries the HTTP status (null for a network
  // failure or timeout). Duck-typed so this module imports nothing at runtime.
  return (
    typeof err === "object" && err !== null && (err as { status?: unknown }).status === 404
  );
}

/**
 * The brief a Ready-to-start card opens (plugin traffic review, 2026-10-06).
 *
 * The list arrives as CARDS — no prefill values, no per-field confidence — so
 * the pane no longer downloads every brief's prefill every 30 s. The full brief
 * is read here, once, when its card is clicked, and laid over the card.
 *
 * Never throws.
 * - A 404 means the brief was launched or ignored since the list was read:
 *   "gone". Opening the form would start a background workflow_prefill LLM run
 *   for an email that is already handled (Ruling R20).
 * - Any other failure (network, timeout, 5xx, a substrate without the tool, a
 *   200 with no item) opens with what the card has: "fallback".
 * - A card that already carries the `prefill_values` KEY is a full row from an
 *   API that predates `compact` (it writes nulls; a card never has the key).
 *   It opens as is, so that API's missing detail route is never read as "gone"
 *   (Ruling R22).
 */
export async function resolveReadyBriefForOpen(
  card: ReadyBriefItem,
  readBrief: ReadBrief,
): Promise<ReadyBriefOpen> {
  if ("prefill_values" in card) return { outcome: "full", brief: card };
  try {
    // The ROW key only. `source_mailbox` is provenance: sent as a row key it
    // would miss a legacy row whose key is blank and 404 a readable brief.
    const detail = await readBrief(card.internet_message_id, card.mailbox_address ?? null);
    return { outcome: "detail", brief: { ...card, ...detail } };
  } catch (err) {
    if (isNotFound(err)) return { outcome: "gone" };
    return { outcome: "fallback", brief: card };
  }
}
